import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Renderer2,
  inject,
  Input,
  OnDestroy,
  PLATFORM_ID,
} from '@angular/core';
import { DOCUMENT, isPlatformServer } from '@angular/common';
import { ThirdPartyScriptsService } from 'third-party-scripts/lib/services/third-party-scripts.service';

@Component({
  selector: 'ss-stx-shared-talkdesk-webchat',
  imports: [],
  template: '<div></div>',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TalkdeskWebchatComponent implements AfterViewInit, OnDestroy {
  render = inject(Renderer2);
  _document = inject(DOCUMENT);
  private platformId = inject(PLATFORM_ID);
  private readonly scriptService = inject(ThirdPartyScriptsService);
  @Input() touchpointId: string = '4b52c30d2ba34617a546fb48d8c90194';

  ngAfterViewInit(): void {
    this.setupWebChat();
  }
  private setupWebChat(): void {
    //https://support.talkdesk.com/hc/en-us/articles/4418031478299-Talkdesk-Digital-Engagement-Customizing-your-Chat-Channel-s-Widget
    if (
      this._document.getElementById('tdWebchatInlineScript') ||
      isPlatformServer(this.platformId)
    ) {
      return;
    }

    this.scriptService.createScriptElement({
      id: 'tdWebchatInlineScript',
      appendToBody: true,
      innerHTML: `
          var webchat;
          ((window, document, node, props, configs) => {
            if (window.TalkdeskChatSDK) {
              return;
            }
            var divContainer = document.createElement("div");
            divContainer.id = node;
            document.body.appendChild(divContainer);
            var src = 'https://talkdeskchatsdk.talkdeskapp.com/v2/talkdeskchatsdk.js';
            var script = document.createElement("script");
            var firstScriptTag = document.getElementsByTagName("script")[0];
            script.type = "text/javascript";
            script.charset = "UTF-8";
            script.id = "tdwebchatscript";
            script.src = src;
            script.async = true;
            firstScriptTag.parentNode.insertBefore(script, firstScriptTag);
            script.onload = () => {
              webchat = TalkdeskChatSDK(node, props);
              webchat.init(configs);
            };
          })(
            window,
            document,
            "tdWebchat",
            { touchpointId: "${this.touchpointId}", accountId: "", region: "td-us-1" },
            ${JSON.stringify(this.buildTalkDeskConfig())}
          );
      `,
    });
  }

  ngOnDestroy(): void {
    if (this._document.getElementById('tdWebchat')) {
      this.render.removeChild(
        this._document.body,
        this._document.getElementById('tdWebchat')
      );
    }
  }

  private buildTalkDeskConfig(): Object {
    const font = 'Brandon,sans-serif';
    const g1 = '#CF9C51';
    const g2 = '#E5C061';
    const g3 = '#9F702C';
    const g4 = '#E0CAA8';
    const g5 = '#ECD18D';
    const b1 = '#202830';
    const b2 = '#382E2C';
    const br1 = '#775421';
    const w1 = '#FFFFFF';
    const w2 = '#F2F4F5';
    const w3 = '#E9E6D5';
    const gr1 = '#AEB3B8';
    return {
      enableValidation: false,
      enableEmoji: false,
      enableUserInput: true,
      enableAttachments: true,
      styles: {
        chatThemeMainFont: font,
        customMessageNoBubbleFontFamily: font,
        welcomeMessageTitleFontFamily: font,
        welcomeMessageContentFontFamily: font,
        customMessageFontFamily: font,
        customFieldLabelFontFamily: font,
        customFieldInputFontFamily: font,
        chatDialogBoxFontFamily: font,
        customDropdownLabelFontFamily: font,
        customDropdownInputFontFamily: font,
        customButtonFontFamily: font,
        chatConfirmButtonFontFamily: font,
        chatCancelButtonFontFamily: font,
        chatLauncherColor: g1,
        chatHoverLauncherColor: g2,
        chatThemeColor: g1,
        chatHoverThemeColor: g2,
        chatTitleTextColor: w1,
        chatSubtitleTextColor: g3,
        chatCloseButtonColor: w1,
        chatMinimizeButtonColor: w1,
        chatHeaderBackgroundColor: g1,
        welcomeMessageBackgroundColor: g4,
        welcomeMessageTitleColor: b1,
        welcomeMessageContentColor: b1,
        customMessageNoBubbleColor: b1,
        customMessageBackgroundColor: w2,
        customMessageColor: b1,
        customFieldLabelColor: b1,
        customFieldInputColor: b1,
        customFieldInputBackgroundColor: w1,
        customDropdownLabelColor: b1,
        customDropdownInputColor: b1,
        customDropdownInputBackgroundColor: w1,
        customButtonColor: g1,
        customButtonBackgroundColor: w1,
        customButtonBorderColor: g1,
        userTextColor: b1,
        userBackgroundColor: g2,
        botTextColor: b1,
        botBackgroundColor: w2,
        userAnchorTextColor: g5,
        userAnchorTextColorVisited: br1,
        botAnchorTextColor: g5,
        botAnchorTextColorVisited: br1,
        avatarForegroundColor: b2,
        avatarBackgroundColor: w3,
        avatarBorderColor: w3,
        userInputSeparatorColor: w2,
        chatDialogBoxIconColor: gr1,
        chatDialogBoxFontColor: b1,
        chatConfirmButtonFontColor: w1,
        chatConfirmButtonBackgroundColor: g1,
        chatCancelButtonFontColor: g1,
        chatCancelButtonBackgroundColor: w2,
        startChatButtonTextColor: w1,
        startChatButtonBackgroundColor: g1,
        startChatButtonBorderColor: g1,
        startChatButtonHoverTextColor: w1,
        startChatButtonHoverBackgroundColor: g2,
        startChatButtonHoverBorderColor: g2,
        carouselTextColor: b1,
        buttonTextColor: w1,
        buttonBackgroundColor: g1,
        buttonHoverBackgroundColor: g2,
        replyTextColor: g1,
        replyBackgroundColor: 'transparent',
        replyBorderColor: g1,
        replyHoverTextColor: g2,
      },
    };
  }
}
